import type { Booking } from '../api/schemas';
import { isSameDay } from './format';

const MINUTE_MS = 60_000;
const WEEK_DAYS = 7;

/** A reservation that is (or was) on the book: a hold is not one yet, a cancelled or released one no longer is. */
const ON_THE_BOOK: ReadonlySet<Booking['status']> = new Set(['confirmed', 'completed', 'paid', 'no_show']);

export type ReservationsSummary = {
  today: number;
  tomorrow: number;
  /** Today and the six days after it. */
  week: number;
  /** The confirmed visit going on now or coming next, when there is one. */
  next?: { booking: Booking; ongoing: boolean };
};

function addDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function startOfDay(date: Date): Date {
  const start = new Date(date.getTime());
  start.setHours(0, 0, 0, 0);
  return start;
}

function endsAt(booking: Booking): number {
  return Date.parse(booking.startsAt) + booking.durationMin * MINUTE_MS;
}

/**
 * What the front desk wants first: how many today, tomorrow and this week, and
 * who is next. Pure, and it never reorders the list it was given.
 */
export function reservationsSummary(bookings: readonly Booking[], now: Date): ReservationsSummary {
  const onBook = bookings.filter((booking) => ON_THE_BOOK.has(booking.status));
  const tomorrow = addDays(now, 1);
  const weekStart = startOfDay(now).getTime();
  const weekEnd = startOfDay(addDays(now, WEEK_DAYS)).getTime();
  const ahead = bookings
    .filter((booking) => booking.status === 'confirmed' && endsAt(booking) > now.getTime())
    .slice()
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const first = ahead[0];
  return {
    today: onBook.filter((booking) => isSameDay(new Date(booking.startsAt), now)).length,
    tomorrow: onBook.filter((booking) => isSameDay(new Date(booking.startsAt), tomorrow)).length,
    week: onBook.filter((booking) => {
      const starts = Date.parse(booking.startsAt);
      return starts >= weekStart && starts < weekEnd;
    }).length,
    ...(first ? { next: { booking: first, ongoing: Date.parse(first.startsAt) <= now.getTime() } } : {}),
  };
}

/** "en 25 min", "en 2 h", "mañana", "el jueves": how soon, the way a front desk says it. */
export function startsIn(start: Date, now: Date): string {
  if (isSameDay(start, now)) {
    const minutes = Math.max(0, Math.round((start.getTime() - now.getTime()) / MINUTE_MS));
    return minutes < 60 ? `en ${minutes} min` : `en ${Math.floor(minutes / 60)} h`;
  }
  if (isSameDay(start, addDays(now, 1))) return 'mañana';
  return `el ${start.toLocaleDateString('es-PA', { weekday: 'long' })}`;
}
